<template>
  <div class="profile">
    <!-- not logged in: sign in with the card right here -->
    <section v-if="!card && !token" class="profile-login">
      <div class="profile-login__card">
        <v-icon size="40" color="#ad282d">mdi-card-account-details-outline</v-icon>
        <h1>Το προφίλ μου</h1>
        <p>Συνδεθείτε με την PARON CARD σας για να δείτε τα στοιχεία, την κάρτα και τις συναλλαγές σας.</p>
        <card-check />
        <div class="profile-login__other">
          Σωματείο ή συνεργάτης; <router-link to="/Login">Είσοδος εδώ</router-link>
        </div>
      </div>
    </section>

    <template v-else>
      <header class="profile-hero">
        <div class="profile-hero__inner">
          <div class="profile-hero__avatar">{{ initialsBadge }}</div>
          <div class="profile-hero__text">
            <div class="profile-hero__hello">Καλώς ήρθες,</div>
            <h1>{{ fullName }}</h1>
            <div class="profile-hero__chips">
              <span v-if="isDealer" class="profile-chip">
                <v-icon x-small color="white">mdi-briefcase-outline</v-icon>Συνεργάτης
              </span>
              <template v-else>
                <span v-if="card" class="profile-chip">
                  <v-icon x-small color="white">mdi-card-account-details-outline</v-icon>Κάρτα {{ card }}
                </span>
                <span class="profile-chip">{{ tier }}</span>
                <span v-if="team" class="profile-chip">
                  <v-icon x-small color="white">mdi-account-group-outline</v-icon>{{ team }}
                </span>
              </template>
            </div>
          </div>
        </div>
      </header>

      <div class="profile-body">
        <nav class="profile-nav" aria-label="Ενότητες προφίλ">
          <button
            v-for="item in menu"
            :key="item.section"
            type="button"
            class="profile-nav__item"
            :class="{ 'profile-nav__item--active': section === item.section }"
            @click="item.route ? $router.push(item.route) : setActiveSection(item.section)"
          >
            <v-icon small>{{ item.icon }}</v-icon>
            <span class="profile-nav__label">{{ item.title }}</span>
            <span class="profile-nav__short">{{ item.short }}</span>
          </button>
          <button type="button" class="profile-nav__item profile-nav__item--logout" @click="logOut()">
            <v-icon small>mdi-logout</v-icon>
            <span class="profile-nav__label">Αποσύνδεση</span>
            <span class="profile-nav__short">Έξοδος</span>
          </button>
        </nav>

        <main ref="panel" class="profile-panel">
          <section v-show="section === 'account_details'">
            <h2 class="profile-panel__title">
              <v-icon color="#ad282d">mdi-card-account-details-outline</v-icon>Πληροφορίες {{ isDealer ? 'συνεργάτη' : 'μέλους' }}
            </h2>
            <dl class="profile-details">
              <template v-if="isDealer">
                <div><dt>Ονοματεπώνυμο</dt><dd>{{ fullName || '-' }}</dd></div>
                <div><dt>Email</dt><dd>{{ (dealer && dealer.email) || '-' }}</dd></div>
              </template>
              <template v-else>
                <div><dt>Ονοματεπώνυμο</dt><dd>{{ fullName }}</dd></div>
                <div><dt>Αριθμός κάρτας</dt><dd>{{ card || '-' }}</dd></div>
                <div><dt>Κατηγορία</dt><dd>{{ tier }}</dd></div>
                <div><dt>Σωματείο</dt><dd>{{ team || '-' }}</dd></div>
              </template>
            </dl>
            <div v-if="!isDealer" class="profile-actions">
              <v-btn color="#1b2f5a" dark depressed @click="setActiveSection('account_wallet')">
                <v-icon left small>mdi-wallet-outline</v-icon>Δείτε την κάρτα σας
              </v-btn>
              <v-btn color="#1b2f5a" outlined to="/Partners">
                <v-icon left small>mdi-tag-outline</v-icon>Εκπτώσεις
              </v-btn>
            </div>
          </section>

          <section v-if="!isDealer" v-show="section === 'account_member_stats'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-chart-box-outline</v-icon>Τα στατιστικά μου</h2>
            <member-stats :transactions="transactions" :created-at="member.createdAt"/>
          </section>

          <section v-if="isDealer" v-show="section === 'account_stats'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-chart-box-outline</v-icon>Στατιστικά</h2>
            <dealer-stats/>
          </section>

          <section v-if="isDealer" v-show="section === 'account_members'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-account-group-outline</v-icon>Μέλη</h2>
            <members-data-route/>
          </section>

          <section v-if="isDealer" v-show="section === 'account_partners'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-domain</v-icon>Συνεργάτες</h2>
            <partners-data-route/>
          </section>

          <section v-show="section === 'account_transactions'">
            <h2 class="profile-panel__title">
              <v-icon color="#ad282d">mdi-receipt-text-outline</v-icon>{{ isDealer ? 'Συναλλαγές' : 'Οι συναλλαγές μου' }}
            </h2>
            <transactions-data-route v-if="isDealer"/>
            <template v-else>
              <div v-if="!transactions.length" class="profile-empty">
                <v-icon size="44" color="rgba(27,47,90,0.35)">mdi-receipt-text-outline</v-icon>
                <p>Δεν υπάρχουν ακόμη συναλλαγές.</p>
                <v-btn color="#1b2f5a" dark depressed :to="{ name: 'QR' }">
                  <v-icon left small>mdi-qrcode-scan</v-icon>Σκανάρετε μια απόδειξη
                </v-btn>
              </div>
              <ul v-else class="receipts">
                <li v-for="transaction in transactions" :key="transaction.id" class="receipt">
                  <div class="receipt__icon"><v-icon color="#1b2f5a">mdi-storefront-outline</v-icon></div>
                  <div class="receipt__main">
                    <div class="receipt__title">{{ transaction.partner ? transaction.partner.title : 'Απόδειξη' }}</div>
                    <div class="receipt__meta">
                      <span>{{ transaction.receipt && transaction.receipt.date ? getDate(transaction.receipt.date) : '-' }}</span>
                      <span v-if="transaction.receipt && transaction.receipt.vat">Α.Φ.Μ. {{ transaction.receipt.vat }}</span>
                    </div>
                  </div>
                  <div class="receipt__side">
                    <div class="receipt__amount">{{ amount(transaction) }}</div>
                    <span
                      v-if="transaction.receipt"
                      class="receipt__status"
                      :class="transaction.receipt.scanCompleted ? 'receipt__status--ok' : 'receipt__status--pending'"
                    >
                      {{ transaction.receipt.scanCompleted ? 'Ολοκληρώθηκε' : 'Εκκρεμεί από ΑΑΔΕ' }}
                    </span>
                  </div>
                </li>
              </ul>
            </template>
          </section>

          <section v-if="!isDealer" v-show="section === 'account_scan'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-qrcode-scan</v-icon>Σκανάρετε απόδειξη</h2>
            <qr-scanner @transaction-created="getTransactions"/>
          </section>

          <section v-if="!isDealer" v-show="section === 'account_wallet'">
            <h2 class="profile-panel__title"><v-icon color="#ad282d">mdi-wallet-outline</v-icon>Πορτοφόλι</h2>
            <wallet v-if="walletOpened"/>
          </section>
        </main>
      </div>
    </template>

    <v-btn
        v-scroll="onScroll"
        v-show="fab"
        fab
        dark
        fixed
        bottom
        right
        color="#1b2f5a"
        aria-label="Επιστροφή στην κορυφή"
        @click="toTop"
      >
        <v-icon color="white">mdi-chevron-up</v-icon>
    </v-btn>
  </div>
</template>


<script>
import CardCheck from '@/components/CardCheck.vue'
import TransactionsDataRoute from '@/routes/TransactionsDataRoute.vue'
import MembersDataRoute from '@/routes/MembersDataRoute.vue'
import PartnersDataRoute from '@/routes/PartnersDataRoute.vue'
import transactionService from '@/services/transaction.js'
import dealerService from '@/services/dealer'
import { formatAthensDate } from '@/utils/date'

// loaded only when those sections are opened: the QR scanner library alone is large
const QrScanner = () => import('@/components/QrScanner.vue')
const Wallet = () => import('@/routes/Wallet.vue')
const DealerStats = () => import('@/components/DealerStats.vue')
const MemberStats = () => import('@/components/MemberStats.vue')

  export default {
    components: { CardCheck, Wallet, QrScanner, DealerStats, MemberStats, TransactionsDataRoute, MembersDataRoute, PartnersDataRoute },
    data() {
        return {
            fab: false,
            transactions: [],
            // dealers start on their figures
            section: this.$store.getters.getDealer ? 'account_stats' : 'account_details',
            // the wallet (and its QR code library) loads the first time it is opened
            walletOpened: false,
            dealer: null
        }
    },
    created() {
      if (this.$store.getters.getDealer) {
        this.getDealer()
      } else if (this.$store.getters.getParonMember) {
        this.getTransactions()
      }
    },
    watch: {
      // logging in with the card form on this page: load the member's transactions right away
      '$store.getters.getParonMember'(member, previous) {
        if (member && !previous) {
          this.getTransactions()
        }
      }
    },
    computed: {
        card() {
            return this.$store.getters.getCard.card
        },
        token() {
            return this.$store.getters.getToken
        },
        // known from the stored login right away; the dealer's details arrive a moment later.
        // Deciding on the loaded details briefly drew the member sections (and the member QR
        // scanner, which then sent the dealer to the login page) for every dealer.
        isDealer() {
            return !!this.$store.getters.getDealer
        },
        initials() {
           return this.$store.getters.getInitials.initials
        },
        member() {
            const paronMember = this.$store.getters.getParonMember
            return (paronMember && paronMember.member) || {}
        },
        fullName() {
            if (this.dealer) {
                return `${this.dealer.firstName || ''} ${this.dealer.lastName || ''}`.trim()
            }
            const first = this.$store.getters.getFirstName.firstName || ''
            const last = this.$store.getters.getLastName.lastName || ''
            return `${first} ${last}`.trim()
        },
        initialsBadge() {
            return this.fullName.split(/\s+/).filter(Boolean).slice(0, 2).map(word => word[0]).join('').toUpperCase() || 'P'
        },
        tier() {
            return this.member.vipGroup || 'Simple Member'
        },
        team() {
            return this.member.team ? this.member.team.title : ''
        },
        menu() {
            if (this.isDealer) {
                return [
                    { section: 'account_stats', title: 'Στατιστικά', short: 'Στατιστικά', icon: 'mdi-chart-box-outline' },
                    { section: 'account_details', title: 'Πληροφορίες', short: 'Στοιχεία', icon: 'mdi-card-account-details-outline' },
                    { section: 'account_transactions', title: 'Συναλλαγές', short: 'Συναλλαγές', icon: 'mdi-receipt-text-outline' },
                    { section: 'account_partners', title: 'Συνεργάτες', short: 'Συνεργάτες', icon: 'mdi-domain' },
                    { section: 'account_members', title: 'Μέλη', short: 'Μέλη', icon: 'mdi-account-group-outline' }
                ]
            }
            return [
                { section: 'account_details', title: 'Πληροφορίες', short: 'Στοιχεία', icon: 'mdi-card-account-details-outline' },
                { section: 'account_member_stats', title: 'Στατιστικά', short: 'Στατιστικά', icon: 'mdi-chart-box-outline' },
                { section: 'account_wallet', title: 'Πορτοφόλι', short: 'Κάρτα', icon: 'mdi-wallet-outline' },
                { section: 'account_transactions', title: 'Συναλλαγές', short: 'Συναλλαγές', icon: 'mdi-receipt-text-outline' },
                { section: 'qr', title: 'Σκανάρετε απόδειξη', short: 'Σκανάρισμα', icon: 'mdi-qrcode-scan', route: { name: 'QR' } }
            ]
        }
    },
    methods: {
      getDealer() {
        dealerService.getDealerById(this.$store.getters.getDealer).then((resp) => {
          this.dealer = resp.dealer
        }).catch(() => {
          // expired or invalid dealer session
          this.logOut()
        })
      },
      setActiveSection(section) {
        this.section = section
        if (section === 'account_wallet') {
          this.walletOpened = true
        }
        // on tablets and phones the menu sits above the panel: bring the panel into view
        if (this.$vuetify.breakpoint.smAndDown && this.$refs.panel) {
          this.$vuetify.goTo(this.$refs.panel, { offset: 70 })
        }
      },
      amount(transaction) {
        const receipt = transaction.receipt
        if (!receipt || receipt.total === null || receipt.total === undefined || receipt.total === '') {
          return '-'
        }
        const value = Number(receipt.total)
        return Number.isNaN(value)
          ? `${receipt.total}€`
          : value.toLocaleString('el-GR', { style: 'currency', currency: 'EUR' })
      },
      toTop() {
        this.$vuetify.goTo(0)
      },
      onScroll (e) {
        if (typeof window === 'undefined') return
        const top = window.pageYOffset ||   e.target.scrollTop || 0
        this.fab = top > 20
      },
      getTransactions() {
        transactionService
        .getTransactionsByMember(
          this.$store.getters.getParonMember.member.id,
          this.$store.getters.getParonMember.number,
          this.$store.getters.getInitials.initials
        )
        .then(response => {
            // newest receipt first; transactions without a receipt date go last
            const time = transaction => (transaction.receipt && Date.parse(transaction.receipt.date)) || 0
            this.transactions = (response.data || []).slice().sort((a, b) => time(b) - time(a))
        })
        .catch((error) => {
            // the stored card login is no longer valid (card changed or deactivated)
            if (error.response && error.response.status === 403) {
              this.logOut()
            }
        })
      },
      logOut() {
        this.$store.dispatch('clearSession')
        this.$router.push({name: 'Login'})
      },
      getDate(date) {
          return formatAthensDate(date)
      }
    }
  }
</script>

<style lang="scss" scoped>
.profile {
    color: #1b2f5a;
    padding-bottom: 64px;
    background: #f6f7fa;
}

/* ---- signed out ---- */
.profile-login {
    padding: 48px 18px 0;
}

.profile-login__card {
    max-width: 560px;
    margin: 0 auto;
    padding: 36px 36px 24px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 18px;
    box-shadow: 0 12px 32px rgba(27, 47, 90, 0.1);
    text-align: center;
}

.profile-login__card h1 {
    margin: 8px 0 4px;
    font-size: 26px;
}

.profile-login__card > p {
    margin: 0 0 20px;
    color: rgba(27, 47, 90, 0.75);
}

.profile-login__other {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(27, 47, 90, 0.1);
    font-size: 15px;
}

.profile-login__other a {
    color: #ad282d;
    font-weight: 700;
}

/* ---- header ---- */
.profile-hero {
    padding: 36px 18px 76px;
    background: linear-gradient(135deg, #1b2f5a 0%, #2c4a86 100%);
    color: #fff;
}

.profile-hero__inner {
    display: flex;
    align-items: center;
    gap: 20px;
    max-width: 1100px;
    margin: 0 auto;
}

.profile-hero__avatar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    border-radius: 50%;
    background: #fff;
    color: #ad282d;
    font-size: 28px;
    font-weight: 700;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.25);
}

.profile-hero__text {
    min-width: 0;
}

.profile-hero__hello {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.8);
}

.profile-hero h1 {
    margin: 2px 0 10px;
    font-size: clamp(24px, 3.4vw, 34px);
    font-weight: 700;
    line-height: 1.2;
}

.profile-hero__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.profile-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    font-size: 13px;
    font-weight: 600;
}

/* ---- body ---- */
.profile-body {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 24px;
    align-items: start;
    max-width: 1100px;
    margin: -48px auto 0;
    padding: 0 18px;
}

.profile-nav {
    position: sticky;
    top: 24px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(27, 47, 90, 0.08);
}

.profile-nav__item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 14px;
    border-radius: 10px;
    color: #1b2f5a;
    font-size: 15px;
    font-weight: 600;
    text-align: left;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.profile-nav__item .v-icon {
    color: inherit;
}

.profile-nav__short {
    display: none;
}

.profile-nav__item:hover {
    background: #f2f4f8;
}

.profile-nav__item--active,
.profile-nav__item--active:hover {
    background: rgba(173, 40, 45, 0.08);
    color: #ad282d;
}

.profile-nav__item--logout {
    margin-top: 6px;
    padding-top: 14px;
    border-top: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 0 0 10px 10px;
    color: rgba(27, 47, 90, 0.7);
}

.profile-panel {
    min-width: 0;
    min-height: 320px;
    padding: 28px 32px;
    background: #fff;
    border: 1px solid rgba(27, 47, 90, 0.1);
    border-radius: 18px;
    box-shadow: 0 8px 24px rgba(27, 47, 90, 0.08);
}

.profile-panel__title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    font-size: 21px;
    font-weight: 700;
}

.profile-details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin: 0;
}

.profile-details > div {
    padding: 14px 16px;
    border-radius: 12px;
    background: #f6f7fa;
}

.profile-details dt {
    font-size: 13px;
    color: rgba(27, 47, 90, 0.65);
}

.profile-details dd {
    margin: 2px 0 0;
    font-size: 17px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.profile-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 24px;
}

.profile-empty {
    padding: 36px 12px;
    text-align: center;
    color: rgba(27, 47, 90, 0.75);
}

.profile-empty p {
    margin: 10px 0 18px;
}

.receipts {
    list-style: none;
    padding: 0 !important;
    margin: 0;
}

.receipt {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 4px;
    border-bottom: 1px solid rgba(27, 47, 90, 0.08);
}

.receipt:last-child {
    border-bottom: 0;
}

.receipt__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #f2f4f8;
}

.receipt__main {
    flex: 1;
    min-width: 0;
}

.receipt__title {
    font-size: 16px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.receipt__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: 13px;
    color: rgba(27, 47, 90, 0.65);
}

.receipt__side {
    flex: 0 0 auto;
    text-align: right;
}

.receipt__amount {
    font-size: 17px;
    font-weight: 700;
}

.receipt__status {
    display: inline-block;
    margin-top: 2px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}

.receipt__status--ok {
    background: rgba(46, 125, 50, 0.1);
    color: #2e7d32;
}

.receipt__status--pending {
    background: rgba(237, 108, 2, 0.12);
    color: #b45309;
}

/* ---- tablets and phones: the menu becomes a row of tabs above the panel ---- */
@media (max-width: 959px) {
    .profile-body {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .profile-nav {
        position: static;
        flex-direction: row;
        overflow-x: auto;
        padding: 6px;
        scrollbar-width: none;
    }

    .profile-nav::-webkit-scrollbar {
        display: none;
    }

    .profile-nav__item {
        flex: 0 0 auto;
        width: auto;
        gap: 6px;
        padding: 10px 12px;
        font-size: 14px;
        white-space: nowrap;
    }

    .profile-nav__item--logout {
        margin: 0 0 0 auto;
        padding-top: 10px;
        border-top: 0;
        border-radius: 10px;
    }
}

@media (max-width: 599px) {
    /* phones: every section fits as an icon with a short label */
    .profile-nav {
        display: grid;
        grid-auto-columns: 1fr;
        grid-auto-flow: column;
        overflow: visible;
        gap: 2px;
    }

    .profile-nav__item,
    .profile-nav__item--logout {
        flex-direction: column;
        gap: 2px;
        margin: 0;
        padding: 8px 2px;
        font-size: 11.5px;
        text-align: center;
        justify-content: center;
    }

    .profile-nav__label {
        display: none;
    }

    .profile-nav__short {
        display: block;
    }

    .profile-hero {
        padding: 24px 18px 64px;
    }

    .profile-hero__inner {
        gap: 14px;
    }

    .profile-hero__avatar {
        width: 60px;
        height: 60px;
        font-size: 22px;
    }

    .profile-panel {
        padding: 20px 16px;
    }

    .profile-details {
        grid-template-columns: 1fr;
    }

    .receipt {
        gap: 10px;
    }

    .receipt__icon {
        display: none;
    }

    .profile-login__card {
        padding: 28px 18px 20px;
    }
}
</style>
